import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  House,
  ClipboardCheck,
  Handshake,
  FileText,
  MessagesSquare,
  Settings,
  X,
} from "lucide-react";
import CommunicationUnreadBadge from "../../common/CommunicationUnreadBadge";
import useCommunicationUnreadCount from "../../common/useCommunicationUnreadCount";
import "./OssPlatformSidebar.css";

export default function OssPlatformSidebar({
  title,
  mobileOpen = false,
  onClose,
  onNavigateRequest,
}) {
  const { t } = useTranslation("oss-sidebar");
  const navigate = useNavigate();
  const location = useLocation();
  const unreadMessagesCount = useCommunicationUnreadCount();

  function go(path) {
    if (onNavigateRequest) onNavigateRequest(path);
    else navigate(path);
    onClose?.();
  }

  const items = [
    {
      key: "dashboard",
      label: t("items.dashboard", "Dashboard"),
      icon: House,
      onClick: () => go("/oss/dashboard"),
      isActive: location.pathname === "/oss" || location.pathname === "/oss/dashboard",
    },
    {
      key: "requests",
      label: t("items.requests", "Requests"),
      icon: ClipboardCheck,
      onClick: () => go("/oss/requests"),
      isActive: location.pathname.startsWith("/oss/requests"),
    },
    {
      key: "projects",
      label: t("items.projects", "Projects"),
      icon: Handshake,
      onClick: () => go("/oss/projects"),
      isActive: location.pathname.startsWith("/oss/projects"),
    },
    {
      key: "documents",
      label: t("items.documents", "Invoices"),
      icon: FileText,
      onClick: () => go("/oss/invoices"),
      isActive: location.pathname.startsWith("/oss/invoices"),
    },
    {
      key: "communication",
      label: t("items.communication", "Communication"),
      icon: MessagesSquare,
      onClick: () => go("/oss/communication"),
      isActive: location.pathname.startsWith("/oss/communication"),
    },
    {
      key: "account",
      label: t("items.account", "Account"),
      icon: Settings,
      onClick: () => go("/oss/account"),
      isActive: location.pathname.startsWith("/oss/account"),
    },
  ];

  const resolvedTitle = title || t("title", "Menu");

  return (
    <>
      <aside className="oss-sidebar">
        <div className="oss-sidebar__panel">
          <div className="oss-sidebar__title">{resolvedTitle}</div>
          <nav className="oss-sidebar__nav" aria-label={t("aria.desktopNav", "Desktop navigation")}>
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={item.onClick}
                  className={`oss-sidebar__item ${item.isActive ? "is-active" : ""}`}
                >
                  <span className="oss-sidebar__icon-wrap">
                    <Icon size={26} strokeWidth={2.1} />
                    {item.key === "communication" && (
                      <span className="legup-communication-unread-icon-overlay">
                        <CommunicationUnreadBadge count={unreadMessagesCount} />
                      </span>
                    )}
                  </span>
                  <span className="oss-sidebar__label">{item.label}</span>
                  {item.key === "communication" && (
                    <span className="legup-communication-unread-inline">
                      <CommunicationUnreadBadge count={unreadMessagesCount} />
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      <div
        className={`oss-sidebar__mobile-overlay ${mobileOpen ? "is-open" : ""}`}
        onClick={onClose}
      />
      <aside className={`oss-sidebar__mobile ${mobileOpen ? "is-open" : ""}`}>
        <div className="oss-sidebar__mobile-header">
          <span className="oss-sidebar__mobile-title">{resolvedTitle}</span>
          <button
            type="button"
            className="oss-sidebar__mobile-close"
            onClick={onClose}
            aria-label={t("aria.closeMenu", "Close menu")}
          >
            <X size={22} />
          </button>
        </div>
        <nav className="oss-sidebar__mobile-nav" aria-label={t("aria.mobileNav", "Mobile navigation")}>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={item.onClick}
                className={`oss-sidebar__mobile-item ${item.isActive ? "is-active" : ""}`}
              >
                <span className="oss-sidebar__icon-wrap">
                  <Icon size={24} strokeWidth={2.1} />
                </span>
                <span className="oss-sidebar__mobile-label">{item.label}</span>
                {item.key === "communication" && (
                  <CommunicationUnreadBadge count={unreadMessagesCount} />
                )}
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
